import { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { Card, ListGroup, Button, Spinner, Form, Row, Col, Modal } from 'react-bootstrap';
import { useAlertContext } from '../../contexts/AlertContext';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import translations from './translations';
import CommentDetailsManager from './CommentDetailsManager';
import PaginationComponent from '../PaginationComponent';
import { FaComments } from 'react-icons/fa';

function CommentDeletedManagement() {
  // Estados principais
  const [allComments, setAllComments] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [performingAction, setPerformingAction] = useState(null);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [commentsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const indexOfLastComment = currentPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;

  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [commentToReopen, setCommentToReopen] = useState(null);

  // Estados para os filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Contextos
  const { isLoggedIn } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;

  // Função de download
  const handleDownload = useCallback((base64String, fileName) => {
    if (!base64String || !fileName) return;

    const link = document.createElement("a");
    let mimeType = "application/octet-stream";
    const parts = fileName.split(".");
    const extension = parts.length > 1 ? parts.pop().toLowerCase() : "";

    switch (extension) {
      case "png": mimeType = "image/png"; break;
      case "jpg":
      case "jpeg": mimeType = "image/jpeg"; break;
      case "gif": mimeType = "image/gif"; break;
      case "bmp": mimeType = "image/bmp"; break;
      case "webp": mimeType = "image/webp"; break;

      case "pdf": mimeType = "application/pdf"; break;
      case "txt": mimeType = "text/plain"; break;
      case "csv": mimeType = "text/csv"; break;
      case "json": mimeType = "application/json"; break;
      case "xml": mimeType = "application/xml"; break;
      case "md": mimeType = "text/markdown"; break;

      case "zip": mimeType = "application/zip"; break;
      case "rar": mimeType = "application/vnd.rar"; break;
      case "7z": mimeType = "application/x-7z-compressed"; break;

      default: mimeType = "application/octet-stream"; break;
    }

    link.href = `data:${mimeType};base64,${base64String}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Função para buscar comentários do backend
  const fetchComments = useCallback(async () => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const auth = localStorage.getItem('auth');
      const response = await axios.get(API_ENDPOINTS.COMMENTS_ALL_DELETED, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      const sortedComments = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAllComments(sortedComments);
    } catch (error) {
      showError(`${t.failedToFetchComments}: ${error.message}`);
      setAllComments([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, t.failedToFetchComments, showError]);

  // Função para manipulação de comentários
  const handleCommentAction = async (commentId, newState) => {
    try {
      setPerformingAction(commentId);
      const auth = localStorage.getItem('auth');
      await axios.put(
        API_ENDPOINTS.COMMENTS_BY_ID_STATE(commentId),
        newState,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json',
          },
        }
      );
      showSuccess(
        t.commentUpdatedSuccessfully
      );
      fetchComments();
    } catch (error) {
      showError(`${t.commentActionError}: ${error.message}`);
    } finally {
      setLoading(false);
      setShowReopenConfirm(false);
      setPerformingAction(null);
    }
    setCommentToReopen("");
  };

  // Efeitos para buscar e filtrar comentários
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    let filteredComments = allComments.filter(
      comment => comment.state !== 'BOOK_OWNER' && comment.state !== 'CHAPTER_OWNER'
    );
    if (startDate) {
      const start = new Date(startDate);
      filteredComments = filteredComments.filter(comment => new Date(comment.createdAt) >= start);
    }
    if (endDate) {
      const end = new Date(endDate + 'T23:59:59.999Z');
      filteredComments = filteredComments.filter(comment => new Date(comment.createdAt) <= end);
    }
    setTotalComments(filteredComments.length);
    setTotalPages(Math.ceil(filteredComments.length / commentsPerPage));
    const startIndex = (currentPage - 1) * commentsPerPage;
    setComments(filteredComments.slice(startIndex, startIndex + commentsPerPage));
  }, [allComments, currentPage, commentsPerPage, startDate, endDate]);

  // Funções de manipulação de filtros e página
  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };



  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2"><div className="d-inline-flex align-items-center gap-2"><FaComments className="text-primary" /> {t.commentManagerDeleted}</div></Card.Header>

        <Card.Body className="border-bottom p-3">
          <h5 className="mb-3">{t.filters}</h5>
          <Form>
            <Row className="align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t.startDate}</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t.endDate}</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button
                  variant="secondary"
                  onClick={handleClearFilters}
                  className="w-100"
                >
                  {t.clearFilters}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>

        <Card.Body>
          {loading ? (
            <div className="text-center py-5 d-flex align-items-center justify-content-center">
              <Spinner animation="border" role="status" className="me-2" />
              <span>{t.loadingComments}</span>
            </div>
          ) : (
            <>
              {comments.length > 0 ? (
                <>
                  {/* Desktop version */}
                  <div className="d-none d-md-block">
                    <ListGroup variant="flush">
                      {comments.map(comment => (
                        <ListGroup.Item key={comment.id} className="border-bottom py-3">
                          <CommentDetailsManager
                            comment={comment}
                            t={t}
                            handleDownload={handleDownload}
                          />
                          {comment.state !== 'TASK' && (
                            <div className="d-flex gap-2 mt-3">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setCommentToReopen(comment)
                                  setShowReopenConfirm(true)
                                }}
                                disabled={performingAction === comment.id}
                              >
                                {performingAction === comment.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  t.reopen
                                )}
                              </Button>

                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    <PaginationComponent
                      totalPages={totalPages}
                      currentPage={currentPage}
                      setCurrentPage={handlePageChange}
                      t={t}
                      lengthTotal={totalComments}
                      tItemName={t.commentsLow}
                      indexOfFirst={indexOfFirstComment}
                      indexOfLast={indexOfLastComment}
                      isMobile={false}
                    />
                  </div>
                  {/* Mobile version */}
                  <div className="d-md-none">
                    {comments.map(comment => (
                      <Card key={comment.id} className="mb-2">
                        <Card.Body className="p-2">
                          <CommentDetailsManager
                            comment={comment}
                            t={t}
                            handleDownload={handleDownload}
                          />
                          {comment.state !== 'TASK' && (
                            <div className="d-flex gap-2 mt-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setCommentToReopen(comment)
                                  setShowReopenConfirm(true)
                                }}
                                disabled={performingAction === comment.id}
                              >
                                {performingAction === comment.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  t.reopen
                                )}
                              </Button>

                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                    <PaginationComponent
                      totalPages={totalPages}
                      currentPage={currentPage}
                      setCurrentPage={handlePageChange}
                      t={t}
                      lengthTotal={totalComments}
                      tItemName={t.commentsLow}
                      indexOfFirst={indexOfFirstComment}
                      indexOfLast={indexOfLastComment}
                      isMobile={true}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  {allComments.length > 0 ? t.noCommentsWithFilters : t.noCommentsFound}
                </div>
              )}

            </>
          )}
        </Card.Body>
      </Card>
      <Modal
        show={showReopenConfirm}
        onHide={() => {
          setShowReopenConfirm(false);
        }}
        centered
      >
        <Modal.Header>
          <Modal.Title>{t.confirmReopen}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t.confirmReopenMessage}
          <br />
          <Card className="mt-3 p-2">
            <CommentDetailsManager
              comment={commentToReopen}
              t={t}
              handleDownload={handleDownload}
            />
          </Card>

        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowReopenConfirm(false); }}
          >
            {t.cancel}
          </Button>
          <Button
            variant="info"
            onClick={() => {
              if (commentToReopen) {
                handleCommentAction(commentToReopen.id, 'BOOK_OWNER');
              }
            }}
          >
            {t.reopen}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CommentDeletedManagement;